<template>
  <div
    v-if="layout === USER_GREETING_LAYOUT.HEADER"
    class="wc-user-greeting wc-user-greeting--header"
  >
    <span class="wc-user-greeting__label">
      <template v-if="isEmployee">{{ $t('salutation') }}, </template>{{ greetingNameFormatted }}
    </span>
    <span v-if="customerDisplayName" class="wc-user-greeting__sublabel">
      {{ $t('forText', { name: customerDisplayName }) }}
    </span>
  </div>
  <div
    v-else-if="layout === USER_GREETING_LAYOUT.ACCOUNT"
    class="wc-user-greeting wc-user-greeting--account"
  >
    {{ $t('salutation') }}, {{ greetingNameFormatted }}
    <span v-if="customerDisplayName" class="wc-user-greeting__subname">
      {{ $t('forText', { name: customerDisplayName }) }}
    </span>
  </div>
</template>

<script setup>
import { computed, defineProps } from 'vue';
import { useStore } from 'vuex';
import { USER_GREETING_LAYOUT } from '@/constants/AppConstants';
import { capitalizeFirstLetter } from '@/utils/formatters';

const props = defineProps({
  layout: {
    type: String,
    default: USER_GREETING_LAYOUT.HEADER,
    validator: value => Object.values(USER_GREETING_LAYOUT).includes(value),
  },
  showCustomerContext: {
    type: Boolean,
    default: true,
  },
});

const store = useStore();

const isCustomer = computed(() => store.getters['user/isCustomer']);
const isEmployee = computed(() => store.getters['user/isEmployee']);
const user = computed(() => store.getters['user/getUser']);

const firstNameFromValue = value => {
  if (!value || typeof value !== 'string') {
    return value;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return value;
  }

  return trimmedValue.split(/\s+/)[0];
};

const greetingNameFormatted = computed(() => {
  let rawName = 'Guest';
  if (isEmployee.value && user.value?.emn) {
    rawName =
      props.layout === USER_GREETING_LAYOUT.HEADER
        ? firstNameFromValue(user.value.emn)
        : user.value.emn;
  } else if (isCustomer.value && user.value?.dispShort) {
    rawName = user.value.dispShort;
  }
  return capitalizeFirstLetter(rawName);
});

const customerDisplayName = computed(() => {
  if (props.showCustomerContext && isEmployee.value && isCustomer.value && user.value?.dispShort) {
    return user.value.dispShort;
  }
  return null;
});
</script>

<style scoped lang="scss">
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

/* Header layout styles */
.wc-user-greeting--header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.wc-user-greeting__label {
  font-size: $font-size-sm;
  line-height: 1.2;
  font-weight: 700;
  max-width: convert-px-to-rem(120);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wc-user-greeting__sublabel {
  font-size: $font-size-xs;
  line-height: 1.2;
  color: var(--gray-700, $gray-700);
  max-width: convert-px-to-rem(120);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Account layout styles */
.wc-user-greeting--account {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
}

.wc-user-greeting__subname {
  font-weight: 400;
  font-size: 0.875rem;
  color: var(--gray-700, $gray-700);
  margin-left: 0.5rem;
}
</style>
